import { useState, useCallback, type ReactNode } from 'react';
import { NotificationProps } from '../components/notification';

export interface NotificationOptions {
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message?: ReactNode;
  duration?: number;
}

export interface NotificationHook {
  notifications: NotificationProps[];
  showNotification: (options: NotificationOptions) => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  // 便捷方法
  showSuccess: (title: string, message?: ReactNode, duration?: number) => void;
  showError: (title: string, message?: ReactNode, duration?: number) => void;
  showInfo: (title: string, message?: ReactNode, duration?: number) => void;
  showWarning: (title: string, message?: ReactNode, duration?: number) => void;
}

export const useNotification = (): NotificationHook => {
  const [notifications, setNotifications] = useState<NotificationProps[]>([]);

  const generateId = useCallback(() => {
    return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const showNotification = useCallback((options: NotificationOptions) => {
    const id = generateId();
    const notification: NotificationProps = {
      id,
      ...options,
      onClose: removeNotification,
    };

    setNotifications(prev => [...prev, notification]);
  }, [generateId]);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // 便捷方法
  const showSuccess = useCallback((title: string, message?: ReactNode, duration?: number) => {
    showNotification({ type: 'success', title, message, duration });
  }, [showNotification]);

  const showError = useCallback((title: string, message?: ReactNode, duration?: number) => {
    showNotification({ type: 'error', title, message, duration });
  }, [showNotification]);

  const showInfo = useCallback((title: string, message?: ReactNode, duration?: number) => {
    showNotification({ type: 'info', title, message, duration });
  }, [showNotification]);

  const showWarning = useCallback((title: string, message?: ReactNode, duration?: number) => {
    showNotification({ type: 'warning', title, message, duration });
  }, [showNotification]);

  return {
    notifications,
    showNotification,
    removeNotification,
    clearAll,
    showSuccess,
    showError,
    showInfo,
    showWarning,
  };
};

 
